import { Card, Icon, IconButton } from "react-native-paper";
import { StyleSheet, View } from "react-native";
import BoardDisplay from "../../../components/BoardDisplay";
import { isEmptyDisplayValue } from "../../../components/FieldDisplay";
import { Task } from "../../../types/types";
import { Field } from "../../../config/companies/config";
import { useAppSelector } from "../../../redux/store";
import {
  calculateDisplayRows,
  shouldHideDisplayValue,
} from "../../../utils/displayRows";
import { getBoardStatusColor } from "../../../utils/boardStatusColor";

interface HorizontalBoardCardProps {
  card: Task;
  sections: { data: Task[]; title: string }[];
  handleCardPress: (card: Task) => void;
  fields: Field[];
  handleStatusChange: (card: Task, direction: "left" | "right") => void;
  handleInColumnMove?: (card: Task, direction: "up" | "down") => void;
  isStatusEditable: (status: string) => boolean;
  statusColors?: Record<string, string>;
}

export const HorizontalBoardCard = ({
  card,
  sections,
  handleCardPress,
  fields,
  handleStatusChange,
  handleInColumnMove,
  isStatusEditable,
  statusColors,
}: HorizontalBoardCardProps) => {
  const userRole = useAppSelector((state) => state.auth.user.role);
  const currentIndex = sections.findIndex((section) =>
    section.data.some((item) => item.id === card.id),
  );
  const currentStatus = sections[currentIndex]?.title;
  const currentSection = sections[currentIndex];
  const currentCardIndex =
    currentSection?.data.findIndex((item) => item.id === card.id) ?? -1;
  const canMoveUpInColumn = !!handleInColumnMove && currentCardIndex > 0;
  const canMoveDownInColumn =
    !!handleInColumnMove &&
    currentCardIndex > -1 &&
    currentCardIndex < (currentSection?.data.length ?? 0) - 1;
  const preparedFieldItems = fields.map((field) => {
    const rawFieldValue = card[field.name] ?? field.defaultValue;
    const normalizedFieldValue =
      Array.isArray(field.options) &&
      rawFieldValue !== null &&
      typeof rawFieldValue !== "undefined" &&
      !field.options.includes(rawFieldValue)
        ? field.defaultValue
        : rawFieldValue;

    return {
      field,
      normalizedFieldValue,
      shouldHide: shouldHideDisplayValue(field, normalizedFieldValue, userRole),
      hasAttachment:
        (field.type === "file" ||
          field.type === "fileAndDocument" ||
          field.type === "document") &&
        normalizedFieldValue &&
        Array.isArray(normalizedFieldValue) &&
        normalizedFieldValue.length > 0,
      flex: field.displayRowFlex,
    };
  });
  const visibleFieldItems = preparedFieldItems.filter(
    (item) => !item.shouldHide,
  );
  const displayRows = calculateDisplayRows(visibleFieldItems);

  const accentColor = getBoardStatusColor(
    currentStatus || card.status,
    statusColors,
  );

  return (
    <Card
      key={card.id}
      mode="outlined"
      style={styles.card}
      onPress={() => handleCardPress(card)}
    >
      <Card.Content style={styles.content}>
        <View style={[styles.accentLine, { backgroundColor: accentColor }]} />

        <View style={styles.body}>
          <View style={styles.dataBlock}>
            {displayRows.map((row, rowIndex) => (
              <View key={`${card.id}-${rowIndex}`} style={styles.displayRow}>
                {row.map(
                  (
                    { field, normalizedFieldValue, hasAttachment, flex },
                    index,
                  ) => {
                    const leadingIcon = field.boardDisplayIcon;
                    const showLeadingIcon =
                      !!leadingIcon?.source &&
                      !isEmptyDisplayValue(field, normalizedFieldValue);

                    return (
                      <View
                        key={field.name}
                        style={[
                          styles.displayRowPart,
                          row.length > 1
                            ? styles.displayRowPartCompact
                            : undefined,
                          { flex },
                          index === row.length - 1 && row.length > 1
                            ? styles.displayRowPartEnd
                            : undefined,
                        ]}
                      >
                        {hasAttachment ? (
                          <View style={styles.attachmentIcon}>
                            <Icon size={20} source="attachment" />
                          </View>
                        ) : (
                          <View style={styles.valueRow}>
                            {showLeadingIcon && leadingIcon && (
                              <Icon
                                source={leadingIcon.source}
                                size={16}
                                color={leadingIcon.color}
                              />
                            )}
                            <View style={{ flex: 1 }}>
                              <BoardDisplay
                                field={field}
                                value={normalizedFieldValue}
                                item={card}
                              />
                            </View>
                          </View>
                        )}
                      </View>
                    );
                  },
                )}
              </View>
            ))}
          </View>
        </View>
      </Card.Content>
      <Card.Actions style={styles.cardActions}>
        <View style={styles.actionGroup}>
          <View style={styles.navButton}>
            <IconButton
              icon="chevron-left"
              size={20}
              onPress={() => handleStatusChange(card, "left")}
              disabled={currentIndex === 0 || !isStatusEditable(currentStatus)}
            />
          </View>
          <View style={styles.navButton}>
            <IconButton
              icon="chevron-right"
              size={20}
              onPress={() => handleStatusChange(card, "right")}
              disabled={
                currentIndex === sections.length - 1 ||
                !isStatusEditable(currentStatus) ||
                !isStatusEditable(sections[currentIndex + 1]?.title)
              }
            />
          </View>
        </View>
        {handleInColumnMove ? (
          <View style={styles.actionGroup}>
            <View style={styles.navButton}>
              <IconButton
                icon="chevron-up"
                size={20}
                onPress={() => handleInColumnMove(card, "up")}
                disabled={!canMoveUpInColumn}
              />
            </View>
            <View style={styles.navButton}>
              <IconButton
                icon="chevron-down"
                size={20}
                onPress={() => handleInColumnMove(card, "down")}
                disabled={!canMoveDownInColumn}
              />
            </View>
          </View>
        ) : null}
      </Card.Actions>
    </Card>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 14,
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  cardActions: {
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingBottom: 6,
  },
  actionGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  navButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: "#F1F5F9",
    alignItems: "center",
    justifyContent: "center",
  },
  attachmentIcon: {
    marginBottom: 4,
    color: "#1976D2",
  },
  displayRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    columnGap: 8,
  },
  displayRowPart: {
    minWidth: 0,
  },
  displayRowPartCompact: {
    alignItems: "flex-start",
  },
  displayRowPartEnd: {
    alignItems: "flex-end",
  },
  content: {
    flexDirection: "row",
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  accentLine: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
  },
  body: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    paddingLeft: 16,
  },
  dataBlock: {
    gap: 8,
  },
  valueRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
});
